import type React from "react"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Save,
  X,
  Users,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import LeadInformationCard from "../../components/forms/LeadInformationCard"
import NotesCard from "../../components/common/NotesCard"
import AssignmentModal from "../../components/modals/AssignmentModal"
import { leadsApi, CreateLeadData } from "../../services/leadsApi"
import { attorneysApi } from "../../services/attorneysApi"
import { firmsApi } from "../../services/firmsApi"
import { useMyContext } from "../../context/Context"

interface Comment {
  id: string
  comment: string
  commented_by: string
  commented_by_display: string
  commented_on: string
  lead: string
}

interface FormErrors {
  [key: string]: string[]
}

export default function AddLeadsModern() {
  const navigate = useNavigate()
  const { user } = useMyContext()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [comments, setComments] = useState<Comment[]>([])
  const [assignedTo, setAssignedTo] = useState<string>("")
  const [assignedName, setAssignedName] = useState<string>("")
  const [assignmentType, setAssignmentType] = useState<"attorney" | "firm" | null>(null)
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false)
  
  // Email and phone validation states
  // COMMENTED OUT - Email validation temporarily disabled
  // const [emailChecking, setEmailChecking] = useState(false)
  // const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  // const [emailMessage, setEmailMessage] = useState("")
  const [phoneChecking, setPhoneChecking] = useState(false)
  const [phoneAvailable, setPhoneAvailable] = useState<boolean | null>(null)
  const [phoneMessage, setPhoneMessage] = useState("")
  const [phoneFormatError, setPhoneFormatError] = useState(false)
  
  // No need to store attorneys and firms - AssignmentModal handles this

  const [formData, setFormData] = useState<CreateLeadData>({
    first_name: "",
    last_name: "",
    phone: "",
    // COMMENTED OUT - Email temporarily disabled
    // email: "",
    source: "calls",
  })

  // No need to load attorneys and firms on mount - AssignmentModal will handle this

  // COMMENTED OUT - Email availability check temporarily disabled
  /*
  // Debounced email availability check
  useEffect(() => {
    if (!formData.email || formData.email.length < 3) {
      setEmailAvailable(null)
      setEmailMessage("")
      return
    }

    // First validate email format
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    
    if (!emailRegex.test(formData.email)) {
      setEmailAvailable(null)
      setEmailMessage("Please enter a valid email address")
      return
    }

    // Additional email format checks
    const [localPart, domain] = formData.email.split('@')
    if (localPart.length > 64) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot exceed 64 characters")
      return
    }
    if (domain.length > 253) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot exceed 253 characters")
      return
    }
    if (localPart.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot be empty")
      return
    }
    if (domain.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot be empty")
      return
    }

    const timer = setTimeout(async () => {
      try {
        setEmailChecking(true)
        const response = await leadsApi.checkLeadEmailAvailability(formData.email)
        setEmailAvailable(response.available)
        setEmailMessage(response.message)
      } catch (error) {
        console.error('Email availability check failed:', error)
        setEmailAvailable(null)
        setEmailMessage("Unable to verify email")
      } finally {
        setEmailChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [formData.email])
  */

  // Debounced phone availability check
  useEffect(() => {
    if (!formData.phone || formData.phone.length < 10) {
      setPhoneAvailable(null)
      setPhoneMessage("")
      return
    }

    // Format validation is now handled by the child component
    // Only check availability here
    const timer = setTimeout(async () => {
      try {
        setPhoneChecking(true)
        const response = await leadsApi.checkLeadPhoneAvailability(formData.phone)
        setPhoneAvailable(response.available)
        setPhoneMessage(response.message)
      } catch (error) {
        console.error('Phone availability check failed:', error)
        setPhoneAvailable(null)
        setPhoneMessage("Unable to verify phone number")
      } finally {
        setPhoneChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [formData.phone])

  // Check if form can be submitted
  const canSubmit = () => {
    // Basic form validation - all required fields must be filled
    const basicValidation = formData.first_name.trim() && 
                           formData.last_name.trim() && 
                           // COMMENTED OUT - Email temporarily disabled
                           // formData.email.trim() && 
                           formData.phone.trim() && 
                           formData.source.trim()
    
    // If basic validation fails, don't check availability
    if (!basicValidation) {
      return false
    }
    
    // Check for any validation errors
    if (Object.keys(errors).length > 0) {
      return false
    }
    
    // Check phone format validation
    if (phoneFormatError) {
      return false
    }
    
    // Only check availability if we have results (not null)
    // COMMENTED OUT - Email validation temporarily disabled
    // const emailValid = emailAvailable === null || emailAvailable === true
    const phoneValid = phoneAvailable === null || phoneAvailable === true
    
    const canSubmitResult = basicValidation && /* emailValid && */ phoneValid && !phoneFormatError
    
    // Debug logging
    if (!canSubmitResult) {
      console.log('Form submission blocked:', {
        firstName: !!formData.first_name.trim(),
        lastName: !!formData.last_name.trim(),
        // email: !!formData.email.trim(), // COMMENTED OUT
        phone: !!formData.phone.trim(),
        source: !!formData.source.trim(),
        // emailAvailable: emailAvailable, // COMMENTED OUT
        phoneAvailable: phoneAvailable,
        phoneFormatError: phoneFormatError,
        noErrors: Object.keys(errors).length === 0,
        errors: Object.keys(errors)
      })
    }
    
    return canSubmitResult
  }

  const handleInputChange = (field: keyof CreateLeadData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: [] }))
    }
    
    // Reset validation states when email or phone changes
    // COMMENTED OUT - Email validation temporarily disabled
    // if (field === 'email') {
    //   setEmailAvailable(null)
    //   setEmailMessage("")
    // } else 
    if (field === 'phone') {
      setPhoneAvailable(null)
      setPhoneMessage("")
    }
    
    // Real-time validation for length limits
    if (field === 'first_name' && value.length > 50) {
      setErrors((prev) => ({ ...prev, first_name: ["First name cannot exceed 50 characters"] }))
    } else if (field === 'last_name' && value.length > 50) {
      setErrors((prev) => ({ ...prev, last_name: ["Last name cannot exceed 50 characters"] }))
    } 
    // COMMENTED OUT - Email validation temporarily disabled
    // else if (field === 'email') {
    //   // Real-time email validation - only check length, format validation is handled by useEffect
    //   if (!value || !value.trim()) {
    //     setErrors((prev) => ({ ...prev, email: ["Email is required"] }))
    //   } else if (value.length > 255) {
    //     setErrors((prev) => ({ ...prev, email: ["Email cannot exceed 255 characters"] }))
    //   } else if (errors.email) {
    //     setErrors((prev) => ({ ...prev, email: [] }))
    //   }
    // } 
    else if (field === 'phone') {
      // Clear any existing phone errors when phone changes
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: [] }))
      }
      // Reset phone format error when phone changes
      setPhoneFormatError(false)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    
    // First name validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = ["First name is required"]
    } else if (formData.first_name.length > 50) {
      newErrors.first_name = ["First name cannot exceed 50 characters"]
    }
    
    // Last name validation
    if (!formData.last_name.trim()) {
      newErrors.last_name = ["Last name is required"]
    } else if (formData.last_name.length > 50) {
      newErrors.last_name = ["Last name cannot exceed 50 characters"]
    }
    
    // COMMENTED OUT - Email validation temporarily disabled
    // Email validation (required)
    // if (!formData.email || !formData.email.trim()) {
    //   newErrors.email = ["Email is required"]
    // } else if (formData.email.length > 255) {
    //   newErrors.email = ["Email cannot exceed 255 characters"]
    // } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(formData.email)) {
    //   newErrors.email = ["Invalid email format"]
    // }
    
    // Phone validation - only check if required, format validation is handled by child component
    if (!formData.phone.trim()) {
      newErrors.phone = ["Phone number is required"]
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // COMMENTED OUT - Email availability check temporarily disabled
    // Check email and phone availability before submitting
    // if (emailAvailable !== true) {
    //   setErrors(prev => ({ ...prev, email: ["Please ensure the email is available before submitting"] }))
    //   return
    // }
    if (phoneAvailable !== true) {
      setErrors(prev => ({ ...prev, phone: ["Please ensure the phone number is available before submitting"] }))
      return
    }

    setLoading(true)

    try {
      // Don't preprocess phone number - let the backend normalize it
      // The backend's normalize_phone_to_e164 handles + prefix and formatting
      
      // Prepare lead data with comments
      const leadData = {
        ...formData,
        phone: formData.phone, // Send as-is, backend will normalize
        comments: comments.map(comment => comment.comment)
      }

      // Create the lead
      const response = await leadsApi.createLead(leadData)
      
      // If there's an assignment, handle it after lead creation
      if (assignedTo && response.lead_id) {
        try {
          if (assignmentType === 'attorney') {
            await leadsApi.assignToAttorney(response.lead_id, assignedTo)
          } else if (assignmentType === 'firm') {
            await leadsApi.referToFirm(response.lead_id, assignedTo)
          }
        } catch (assignmentError) {
          console.error("Failed to assign lead:", assignmentError)
          // Don't fail the entire operation if assignment fails
        }
      }
      
      // Success - navigate back
      navigate("/app/leads")
    } catch (error: any) {
      console.error("Error saving lead:", error)
      
      // Handle API errors and show them inline
      // The apiMiddleware puts the actual error response in error.data
      if (error.data && error.data.errors) {
        // Field-specific validation errors from backend
        setErrors(error.data.errors)
      } else if (error.data && error.data.message) {
        // General error message from backend
        setErrors({ general: [error.data.message] })
      } else if (error.message) {
        // Fallback to error message
        setErrors({ general: [error.message] })
      } else {
        setErrors({ general: ["Failed to create lead. Please try again."] })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate("/app/leads")
  }

  const handleAssignmentModalOpen = (type: "attorney" | "firm") => {
    setAssignmentType(type)
    setAssignmentModalOpen(true)
  }

  const handleAssignment = async (type: "attorney" | "firm", id: string) => {
    setAssignmentType(type)
    setAssignedTo(id)
    
    // Fetch the name based on the type and ID
    try {
      if (type === "attorney") {
        const attorneyResponse = await attorneysApi.getAttorney(id)
        const attorney = attorneyResponse.attorney || attorneyResponse
        setAssignedName(attorney.name)
      } else if (type === "firm") {
        const firmResponse = await firmsApi.getFirm(id)
        const firm = firmResponse.firm || firmResponse
        // For firms, show the person's name (first_name + last_name)
        setAssignedName(`${firm.first_name} ${firm.last_name}`)
      }
    } catch (error) {
      console.error("Failed to fetch assignment details:", error)
      setAssignedName("Unknown")
    }
  }

  const getAssignmentDisplayText = () => {
    if (!assignedTo || !assignmentType) return "No assignment"
    
    if (assignmentType === 'attorney') {
      return `Attorney: ${assignedName || assignedTo}`
    } else {
      return `Firm: ${assignedName || assignedTo}`
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={handleCancel} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Leads
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Add New Lead</h1>
          <p className="text-slate-600">Create a new lead with the required information</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Contact Information */}
                      <LeadInformationCard
              leadData={{
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone: formData.phone,
                // COMMENTED OUT - Email temporarily disabled
                // email: formData.email,
                source: formData.source,
              }}
            viewOnly={false}
            errors={errors}
            onDataChange={(field, value) => handleInputChange(field, value)}
            alwaysEditable={true}
            // COMMENTED OUT - Email validation temporarily disabled
            // emailValidation={{
            //   emailChecking,
            //   emailAvailable,
            //   emailMessage
            // }}
            phoneValidation={{
              phoneChecking,
              phoneAvailable,
              phoneMessage
            }}
            onPhoneFormatValidationChange={setPhoneFormatError}
          />

          <div className="space-y-6">
            {/* Notes */}
            <NotesCard
              comments={comments}
              onAddComment={(comment) => {
                // Get current user info from context
                const currentUser = user?.full_name || user?.email || 'Current User'
                const newComment: Comment = {
                  id: Date.now().toString(),
                  comment: comment,
                  commented_by: currentUser,
                  commented_by_display: currentUser,
                  commented_on: new Date().toISOString(),
                  lead: "new-lead",
                }
                setComments((prev) => [newComment, ...prev])
              }}
            />

            {/* Assignment */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Assign Lead
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="text-sm text-slate-600">
                      Current assignment: <span className="font-medium">{getAssignmentDisplayText()}</span>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAssignmentModalOpen("attorney")}
                        className="flex-1"
                      >
                        Assign to Attorney
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAssignmentModalOpen("firm")}
                        className="flex-1"
                      >
                        Refer to Firm
                      </Button>
                    </div>
                    
                    {assignedTo && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setAssignedTo("")
                          setAssignedName("")
                          setAssignmentType(null)
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Clear Assignment
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Error Message */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{errors.general[0]}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !canSubmit()} className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed">
              <Save className="h-4 w-4 mr-2" />
              {loading ? "Saving..." : "Save Lead"}
            </Button>
          </div>
        </form>

        {/* Assignment Modal */}
        <AssignmentModal
          isOpen={assignmentModalOpen}
          onClose={() => setAssignmentModalOpen(false)}
          assignmentType={assignmentType}
          onAssignment={handleAssignment}
        />
      </div>
    </div>
  )
}
